// we create a site.ts file to mimic a brand site setup. All this does is import the entire DS Core library, but it let us build with the same setup
import { Container, decorate, inject, injectable, unmanaged } from "inversify";
import { Startup } from "./startup";
import { ApiClient, IApiClient, IApiClientId, PlatformSettings, PlatformClient, IPlatformClient, IPlatformClientId } from "@se/platform"
import { SessionService } from "./services/implementations/sessionService";
import { ISessionService, ISessionServiceId } from "./services/interfaces/sessionService";
import { Environment } from "./environments/environment";
import { DesignSystemConfig } from "./configuration/designSystemConfig";
import { IAddressLookupService, IAddressLookupServiceId } from "./services/interfaces/addressLookupService";
import { AddressLookupService } from "./services/implementations/addressLookupService";
import { IMessagingService, IMessagingServiceId, MessagingService } from "./services";
import { ICookieService, ICookieServiceId } from "./services/interfaces/cookieService";
import { CookieService } from "./services/implementations/cookieService";
import { IEnquiryFormService, IEnquiryFormServiceId } from "./services/interfaces/enquiryFormService";
import { EnquiryFormService } from "./services/implementations/enquiryFormService";
import { ILoggingServiceId, ILoggingService } from "./services/interfaces/loggingService";
import { LoggingService } from "./services/implementations/loggingService";
import { IAnalyticsService, IAnalyticsServiceId } from "./services/interfaces/analyticsService";
import { AnalyticsService } from "./services/implementations/analyticsService";
import { IHashingService, IHashingServiceId } from "./services/interfaces/hashingService";
import { HashingService } from "./services/implementations/hashingService";

const getContainer = function (environment: Environment): Container {

    // configure the platform clients to allow them to be injected into our components and services
    decorate(injectable(), ApiClient);
    decorate(injectable(), PlatformClient);
    decorate(injectable(), PlatformSettings);
    decorate(inject(PlatformSettings), PlatformClient, 0);

    const container = new Container();

    container.bind(Startup).toSelf().inSingletonScope();
    container.bind<DesignSystemConfig>(DesignSystemConfig).toConstantValue(environment.designSystemConfig);

    container.bind<ISessionService>(ISessionServiceId).to(SessionService).inSingletonScope();
    container.bind<IMessagingService>(IMessagingServiceId).to(MessagingService).inSingletonScope();
    container.bind<IAddressLookupService>(IAddressLookupServiceId).to(AddressLookupService).inSingletonScope();
    container.bind<IEnquiryFormService>(IEnquiryFormServiceId).to(EnquiryFormService).inSingletonScope();
    container.bind<ICookieService>(ICookieServiceId).to(CookieService).inSingletonScope();
    container.bind<IAnalyticsService>(IAnalyticsServiceId).to(AnalyticsService).inSingletonScope();
    container.bind<IHashingService>(IHashingServiceId).to(HashingService).inSingletonScope();

    // register platform dependencies
    container.bind<IApiClient>(IApiClientId).to(ApiClient).inSingletonScope();
    container.bind <Gtag.Gtag> (Symbol.for("Gtag.Gtag")).toFunction(window.gtag);

    container.bind<PlatformSettings>(PlatformSettings).toConstantValue(environment.platformSettings);
    container.bind<IPlatformClient>(IPlatformClientId).to(PlatformClient).inSingletonScope();

    //setup MS's JS based app insights as a singleton service for the app
    container.bind<ILoggingService>(ILoggingServiceId).to(LoggingService).inSingletonScope();

    return container;
}

export { getContainer };